import React, { useState, Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import T from 'prop-types';
import SimpleFormat from '@16g/react-simple-format';
import { isArray, isEmpty, pick } from 'lodash-es';

import AlertsDropdown from './AlertsDropdown';
import InsightsDetails from '../InsightsDetails';
import AppIcon from '../../../../../components/AppIcon';
import Container from '../../../../../components/Container';
import AddToList from '../../../../../components/AddToList';
import CollapsibleSlot from '../../../../../components/CollapsibleSlot';
import HistoryBackButton from '../../../../../components/Buttons/HistoryBackButton';
import { AppInsightsNavigationConnector } from '../../../../../components/AppSwitcher';
import { MARKET_LIST_TYPE } from '../../../../../components/AddToList/listTypeConfig';
import QuickViewContext from '../../../../../components/QuickView/QuickViewContext';
import CsvExportButton from '../../../../../components/Buttons/CsvExportButton';
import { StoreDropdown } from '../../../../../components/SwitchStores';
import ExportsViewProvider from '../../../../../components/AdvancedExportModal';

import { ASSETS, SUB_ASSETS } from '../../config/assets';
import { notImplementedExports, pageNamesMapping } from '../../config/csv-export';
import axios from '../../../../../configs/axios';
import helpers, {
  isUnified,
  truncateTicker,
  refNoByStore,
  generateDateTimeSuffix,
} from '../../../../../utils/helpers';
import { useRefValue } from '../../../../../hooks';
import { initParams } from '../../../app-overview/_shared/config/export-params';
import { yesterday, subDays, format, ISODateFormat } from '../../../../../utils/date';

const { app_details } = ASSETS;

const normalizedStore = {
  google_play: 'Google Play',
  itunes_connect: 'iOS App Store',
};

const InsightsHeader = ({
  app,
  permissions,
  filters = {},
  pageName,
  withUnifiedSupport = false,
  appNavigationProps,
  renderShadowHeader,
  onPageStoreChange,
}) => {
  const [currentApp, setCurrentApp] = useState(app);
  const appHasUnified = Object.keys(currentApp.ref_nos_by_store).some(isUnified);
  const showStoreSwitcher = Object.keys(currentApp.ref_nos_by_store).length > 1;

  const { onPubQuickViewOpen, onCompanyQuickViewOpen } = useContext(QuickViewContext);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [settings, setSettings] = useState({ pending: false, error: false });
  const [store, setStore] = useState(
    isUnified(currentApp.store) ? 'itunes_connect' : currentApp.store
  );
  const [details, setDetails] = useState({ itunes_connect: {}, google_play: {} });
  const [csvExport, setCsvExport] = useState({
    csvExportUrl: '',
    disabled: false,
    csvHttpMethod: 'get',
    csvPostParams: {},
  });
  const appStore = filters.store || store;

  const appDetails = useRefValue(details);

  const pub = useMemo(() => {
    let publisherByStore = currentApp.publishers_by_store[appStore];
    let selectedStore = appStore;
    if (selectedStore === 'unified' && !publisherByStore) {
      selectedStore = 'itunes_connect';
      publisherByStore = currentApp.publishers_by_store.itunes_connect;
    }
    return {
      store: selectedStore,
      ref_no: publisherByStore.publisher_id,
      publisher_name: publisherByStore.publisher_name,
    };
  }, [appStore, currentApp.publishers_by_store]);

  const handleSetCsvUrl = ({
    detail: { csvExportUrl, disabled, httpMethod = 'get', csvPostParams = {} },
  }) =>
    setCsvExport({
      csvExportUrl,
      disabled,
      csvHttpMethod: httpMethod,
      csvPostParams,
    });

  useEffect(() => {
    document.addEventListener('on-getting-csv-url', handleSetCsvUrl);
    return () => {
      document.removeEventListener('on-getting-csv-url', handleSetCsvUrl);
    };
  }, []);

  const handleStoreChange = useCallback((value = store) => {
    setStore(value);
    setSettings({ ...settings, pending: true });
    if (!isEmpty(appDetails.current[value])) {
      setSettings({ error: false, pending: false });
      return;
    }
    axios
      .post(`/apps/${currentApp.store}/${currentApp.ref_no}/overview/data.json`, {
        request: {
          assets: {
            app_details: {
              assets: SUB_ASSETS[app_details],
              filters: { store: value },
            },
          },
        },
      })
      .then(({ data }) => {
        setDetails({ ...appDetails.current, [value]: data.assets.app_details });
        setSettings({ error: false, pending: false });
      })
      .catch(() => {
        setSettings({ error: true, pending: false });
      });
  }, []);

  const handlePageStoreChange = (value) => {
    onPageStoreChange(value);
    setSettings({ ...settings, pending: true });
    axios
      .get(`/apps/${value}/${refNoByStore(value)}/summary.json`)
      .then(({ data }) => {
        setCurrentApp(data);
        setSettings({ error: false, pending: false });
      })
      .catch(() => {
        setSettings({ error: true, pending: false });
      });
  };

  const handlePubQuickViewOpen = () => {
    onPubQuickViewOpen(pub);
  };

  const handleCompanyQuickViewOpen = () => {
    onCompanyQuickViewOpen({ ref_no: currentApp.company_id });
  };

  const isExportPresent = () => !notImplementedExports.includes(pageName);

  const exportOption =
    isExportPresent() && !permissions.allowAdvancedExportModal
      ? [
          {
            key: 'csv-export',
            item: (
              <CsvExportButton
                url={csvExport.csvExportUrl}
                variant="light"
                isSquare={false}
                disabled={csvExport.disabled}
                allowCsvExport={!!csvExport.csvExportUrl}
                buttonText="Basic Export"
                featureNameText={pageNamesMapping[pageName]}
                method={csvExport.csvHttpMethod === 'post' ? csvExport.csvHttpMethod : undefined}
                body={csvExport.csvPostParams}
              />
            ),
          },
        ]
      : [];

  const prependCustomOptions = [
    {
      key: 'add-to-list',
      item: (
        <AddToList
          entities={[
            {
              ...app,
              store: appStore,
              ref_no: currentApp.ref_nos_by_store[appStore === 'unified' ? 'both' : appStore],
              kind: appStore === 'unified' ? 'unified_app' : 'app',
            },
          ]}
          listType={MARKET_LIST_TYPE}
          currentUserId={permissions.current_user_id}
          customerId={permissions.customer_id}
          buttonText="Add to Market"
          variant="light"
          icon="hicon-plus-alt"
          className="add-to-market-menu-item"
        />
      ),
    },
  ];

  const customOptions = [
    ...exportOption,
    ...(isUnified(appStore) ? ['itunes_connect', 'google_play'] : [appStore]).map(
      (currentStore) => ({
        key: currentStore,
        item: (
          <a
            href={
              currentStore === 'itunes_connect'
                ? currentApp.app_store_url
                : currentApp.google_play_url
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="hicon-link-out-rounded" />
            {`View in ${normalizedStore[currentStore]}`}
          </a>
        ),
      })
    ),
  ];

  const storeSwitcher = showStoreSwitcher && (
    <StoreDropdown
      menuAlign="end"
      active={appStore}
      withUnified={withUnifiedSupport && appHasUnified}
      onChange={handlePageStoreChange}
    />
  );

  const reportParams = {
    start_date: format(
      filters.period_from_date || subDays(filters.period_to_date, filters.period),
      ISODateFormat
    ),
    end_date: format(filters.period_to_date, ISODateFormat),
    name: `${currentApp.name}_${generateDateTimeSuffix()}`,
    entities: [
      {
        ...app,
        kind: 'app',
        store: filters.store,
        ref_no: currentApp.ref_nos_by_store[filters.store === 'unified' ? 'both' : filters.store],
        value: currentApp.name,
      },
    ],
    ...(pageName === 'rank_history_show'
      ? {
          countries_iso: isArray(filters.country_iso) ? filters.country_iso : ['US'],
        }
      : {}),
    ...(['app_insights_summary_show'].includes(pageName)
      ? {
          start_date: format(subDays(yesterday(), 90), ISODateFormat),
          end_date: format(yesterday(), ISODateFormat),
        }
      : {}),
  };

  return (
    <Fragment>
      {renderShadowHeader && renderShadowHeader({ storeSwitcher })}
      <ExportsViewProvider
        initParams={pick(initParams, ['maxDate', 'maxPeriod', 'permissions', 'pageName'])}
        reportParams={reportParams}
      >
        <div className="app-insights-header">
          {app && currentApp.name && (
            <Container maxWidth={1128}>
              <HistoryBackButton />
              <div className="app-insights-header__inner">
                <div className="app-insights-header__left">
                  <AppIcon
                    containerClasses="app-insights-header__app-icon"
                    alt={currentApp.name}
                    url={currentApp.icon_url_2x}
                    url2x={currentApp.icon_url_2x}
                    store={currentApp.store}
                    size={80}
                    withBorder
                  />
                  <div className="app-insights-header__meta-content">
                    <div className="app-insights-header__app-company-data">
                      <h1 className="h3 text-truncate">
                        {helpers.decodeEntities(currentApp.name)}
                      </h1>
                      {currentApp.company_ticker && (
                        <a
                          className="company-name-ticker cursor-pointer ignore-onclickoutside"
                          onClick={handleCompanyQuickViewOpen}
                        >
                          {truncateTicker(currentApp.company_ticker)}
                        </a>
                      )}
                    </div>
                    <div className="app-insights-header__more-links">
                      {pub.publisher_name && (
                        <>
                          <a
                            className="publisher-name ignore-onclickoutside"
                            {...(permissions.allowAppQuickView
                              ? {
                                  onClick: handlePubQuickViewOpen,
                                }
                              : {
                                  href: `/publishers/${pub.store}/${pub.ref_no}`,
                                })}
                          >
                            <span className="publisher-name__label">Publisher:</span>
                            <SimpleFormat
                              text={pub.publisher_name}
                              className="publisher-name__value text-truncate"
                            />
                          </a>
                          <span className="dot-divider">·</span>
                        </>
                      )}
                      {currentApp.company_name && (
                        <>
                          <a
                            className="company-name ignore-onclickoutside"
                            {...(permissions.allowAppQuickView
                              ? {
                                  onClick: handleCompanyQuickViewOpen,
                                }
                              : {
                                  href: `/companies/${currentApp.company_id}`,
                                })}
                          >
                            <span className="publisher-name__label">Company:</span>
                            <span className="company-name__value text-truncate cursor-pointer">
                              {currentApp.company_name}
                            </span>
                          </a>
                          <span className="dot-divider">·</span>
                        </>
                      )}
                      <span
                        className="app-insights-header__show-more link-default"
                        onClick={() => {
                          setDetailsOpen((prev) => !prev);
                          handleStoreChange();
                        }}
                      >
                        <span className="notranslate">
                          {!detailsOpen ? 'Show Details' : 'Hide Details'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="app-insights-header__right">
                  <div className="app-insights-header__market-alert">
                    {storeSwitcher}
                    <AlertsDropdown
                      prependCustomOptions={prependCustomOptions}
                      appendCustomOptions={customOptions}
                    />
                  </div>
                </div>
              </div>
              <AppInsightsNavigationConnector
                variant="tabs"
                store={filters.store}
                {...appNavigationProps}
              />
            </Container>
          )}
        </div>
      </ExportsViewProvider>
      <CollapsibleSlot show={detailsOpen} animationType="" className="app-insights-details-drawer">
        <InsightsDetails
          open={detailsOpen}
          details={details[store]}
          settings={settings}
          store={store}
          app={app}
          handleStoreChange={handleStoreChange}
          onClose={() => setDetailsOpen(false)}
        />
      </CollapsibleSlot>
    </Fragment>
  );
};

InsightsHeader.propTypes = {
  app: T.object,
  permissions: T.object,
  filters: T.shape({
    store: T.string,
  }),
  pageName: T.string,
  withUnifiedSupport: T.bool,
  appNavigationProps: T.object,
  renderShadowHeader: T.func,
  onPageStoreChange: T.func,
};

export default InsightsHeader;
